import { useEffect } from "react";
import { keepAwake } from "@/app/platform";

/**
 * THE SCREEN STAYS LIT FOR AS LONG AS THE APP IS IN FRONT.
 *
 * ═══ WHY THE WHOLE APP AND NOT THE CLOCK ═══
 *
 * The lock used to belong to the timer, taken at the start of a countdown and handed back the
 * moment the piece ended or the athlete left the screen. That is correct for a clock and wrong
 * for this app: the phone is on the floor two metres away with chalk on the athlete's hands for
 * the whole session, not only during a metcon. The screen going dark while they are reading the
 * next set off the lift log, or logging what they just did, costs the same walk and the same tap
 * as it does mid-piece — and it is a worse surprise, because nothing is obviously "running".
 *
 * So the lock is held here, once, above everything, for as long as the app is mounted.
 *
 * ═══ ⚠️ "IN FRONT" IS THE PLATFORM'S JOB, NOT THIS HOOK'S ═══
 *
 * Nothing here listens for the app being backgrounded, and that is deliberate on both halves:
 *
 * · ON DEVICE the flag is `UIApplication.isIdleTimerDisabled`, which iOS only honours for the
 *   FOREGROUND app. Holding it while backgrounded keeps nothing awake, so releasing it there
 *   would be ceremony with a race in it — and re-taking it on resume is a frame of work on the
 *   one path that has to feel instant.
 * · ON THE WEB the browser revokes a screen lock whenever the page is hidden and never returns
 *   it. `app/platform/keep-awake.ts` already carries the `visibilitychange` listener that takes
 *   it back, and that listener lives for exactly as long as something wants the screen — which
 *   is now the lifetime of the app.
 *
 * The result is the same sentence on both: lit while it is in front, free to sleep when it is
 * not, with neither platform's rule restated up here where it would drift.
 *
 * ⚠️ A REFUSED LOCK IS NOT AN ERROR AND IS NOT REPORTED. Low battery, a browser without the
 * API, an insecure origin, a simulator: the seam answers `false` rather than throwing, and the
 * only consequence is a screen that may dim. Telling somebody about it mid-session would be
 * worse than the dimming, and there is nothing they could do.
 */
export function useScreenAwake(): void {
  useEffect(() => {
    void keepAwake.enable();

    /**
     * ⚠️ RELEASED ON UNMOUNT, EVEN THOUGH THIS IS MOUNTED FOR THE LIFE OF THE APP. It costs one
     * line and it is the difference between a clean teardown and a phone that never dims again
     * — in a test that mounts the tree twice, in a hot reload, and on the web build, where a
     * lock left behind outlives the React tree that took it.
     */
    return () => {
      void keepAwake.disable();
    };
  }, []);
}
